import type { LOGGER_LEVEL } from '@aircall/logger';
import { Logger as AircallLogger, LOGGER_ENVIRONMENT } from '@aircall/logger';
import { StatusType } from '@datadog/browser-logs';
import type { UserInfo } from '@entities/user';
import type { UserRepository } from '@infra/repositories/user.repository';
import { RepositoryFactory } from '@main/factories/repository.factory';

type ToupleType = Collection | string | object | unknown;

interface Collection {
  [k: string]: ToupleType | ToupleType[];
}

export class Logger extends AircallLogger {
  private static ins: Logger;

  static level = (process.env.LOGGER_LEVEL as StatusType) || StatusType.info;

  private sensitiveKeys: string[] = [
    'password',
    'token',
    'email',
    'refreshToken',
    'idToken',
  ];

  private userRepository!: UserRepository;

  constructor() {
    /**
     * TODO: Replace this with dependency injection to create singleton instance
     */
    super();
    if (Logger.ins) {
      return Logger.ins;
    }

    this.userRepository = RepositoryFactory.buildMemUserRepository();
    this.setUpLogger();
    return (Logger.ins = this);
  }

  private setUpLogger() {
    const service = 'salesforce-scv-connector';
    const environment =
      (process.env.MODE as LOGGER_ENVIRONMENT) ||
      LOGGER_ENVIRONMENT.DEVELOPMENT;

    const user: UserInfo | undefined = this.userRepository.getUserInfo();

    this.init({
      context: {
        service,
        user_session: {
          device: navigator.userAgent,
          environment,
          release: '',
        },
      },
      level: Logger.level,
      token: process.env.DATADOG_TOKEN,
      version: '1.0.0',
      environment,
      service: service,
      user: {
        id: user?.id,
        company_id: user?.company_id,
      },
    });
  }

  private isKeySensitive(key: string): boolean {
    return !!this.sensitiveKeys.find(
      (sensitiveKey) => sensitiveKey.toLowerCase() === key.toLowerCase(),
    );
  }

  private hideSensitiveData = (properties?: Collection) => {
    if (properties) {
      if (Array.isArray(properties)) {
        properties.forEach((prop) => this.hideSensitiveData(prop));
      } else if (typeof properties === 'object') {
        Object.keys(properties).forEach((key) => {
          this.hideSensitiveData(<Collection>properties[key]);
          if (typeof properties[key] !== 'object' && this.isKeySensitive(key)) {
            properties[key] = '<sensitive**>';
          }
        });
      }
    }
  };

  override log(
    level: LOGGER_LEVEL,
    message: string,
    properties?: Collection,
  ): void {
    properties = JSON.parse(JSON.stringify(properties));
    this.hideSensitiveData(properties);
    super.log(level, message, properties);
  }

  override info(message: string, properties?: Collection): void {
    properties = JSON.parse(JSON.stringify(properties));
    this.hideSensitiveData(properties);
    super.info(message, properties);
  }

  override debug(message: string, properties?: Collection): void {
    properties = JSON.parse(JSON.stringify(properties));
    this.hideSensitiveData(properties);
    super.debug(message, properties);
  }

  override warn(message: string, properties?: Collection): void {
    properties = JSON.parse(JSON.stringify(properties));
    this.hideSensitiveData(properties);
    super.warn(message, properties);
  }

  override error(message: string, properties?: Collection): void {
    properties = JSON.parse(JSON.stringify(properties));
    this.hideSensitiveData(properties);
    super.error(message, properties);
  }
}
